import Swal from 'sweetalert2';
import withReactContent from 'sweetalert2-react-content';
const MySwal = withReactContent(Swal);

import React, { useState, useEffect } from 'react';
import { useAuth } from '@/context/AuthContext';
import Layout from '@components/Layout';
import { db } from '@root/firebase'; // Firebase initialization
import ReactQuill from 'react-quill';
import 'quill/dist/quill.snow.css';
import Modal from '@/components/Modal';

import { toast } from 'react-hot-toast';
import {
  collection,
  addDoc,
  updateDoc,
  deleteDoc,
  doc,
  getDocs,
  query,
  orderBy,
} from 'firebase/firestore';
import 'react-quill/dist/quill.snow.css';

function DirectoryAdmin() {
  const { globalUser, logout } = useAuth();
  const [directories, setDirectories] = useState([]);
  const [newDirectoryName, setNewDirectoryName] = useState('');
  const [newSubDirectoryName, setNewSubDirectoryName] = useState('');
  const [loading, setLoading] = useState(true);
  const [activeDirectoryId, setActiveDirectoryId] = useState(null);
  const [activeChild, setActiveChild] = useState(null); // Track selected child directory
  const [editorContent, setEditorContent] = useState(''); // HTML content for editor
  const [isDeleting, setIsDeleting] = useState(false);
  const [selectedDirectoryId, setSelectedDirectoryId] = useState('');
  const [selectedSubDirectoryId, setSelectedSubDirectoryId] = useState('');
  const [deletedFlag, setDelectedFlag] = useState('');

  useEffect(() => {
    const fetchDirectories = async () => {
      setLoading(true);
      const q = query(collection(db, 'directory'), orderBy('order'));
      const querySnapshot = await getDocs(q);
      const dirs = querySnapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));
      setDirectories(dirs);
      setLoading(false);
    };
    fetchDirectories();
  }, []);

  const toggleAccordion = (id) => {
    setActiveDirectoryId(activeDirectoryId === id ? null : id);
  };

  const handleCreateDirectory = async () => {
    if (!newDirectoryName) return;
    const newDirectory = {
      name: newDirectoryName,
      parent: null,
      order: directories.filter((dir) => !dir.parent).length,
    };
    const docRef = await addDoc(collection(db, 'directory'), newDirectory);
    setDirectories([...directories, { id: docRef.id, ...newDirectory }]);
    setNewDirectoryName('');
  };

  const handleCreateSubDirectory = async (parentId) => {
    if (!newSubDirectoryName) return;
    const newDirectory = {
      name: newSubDirectoryName,
      parent: parentId,
      order: directories.filter((dir) => dir.parent === parentId).length,
    };
    const docRef = await addDoc(collection(db, 'directory'), newDirectory);
    setDirectories([...directories, { id: docRef.id, ...newDirectory }]);
    setNewSubDirectoryName('');
  };

  const handleDeleteDirectory = async (id) => {
    await deleteDoc(doc(db, 'directory', id));
    setDirectories(directories.filter((dir) => dir.id !== id));
  };

  const handleReorderDirectory = async (id, direction, parentId = null) => {
    const siblingDirectories = directories
      .filter((dir) => dir.parent === parentId)
      .sort((a, b) => a.order - b.order);

    const currentIndex = siblingDirectories.findIndex((dir) => dir.id === id);
    if (currentIndex === -1) return;

    const targetIndex =
      direction === 'up' ? currentIndex - 1 : currentIndex + 1;
    if (targetIndex < 0 || targetIndex >= siblingDirectories.length) return;

    const currentDirectory = siblingDirectories[currentIndex];
    const targetDirectory = siblingDirectories[targetIndex];

    // Swap orders
    await updateDoc(doc(db, 'directory', currentDirectory.id), {
      order: targetDirectory.order,
    });
    await updateDoc(doc(db, 'directory', targetDirectory.id), {
      order: currentDirectory.order,
    });

    setDirectories((prevDirectories) =>
      prevDirectories.map((dir) => {
        if (dir.id === currentDirectory.id)
          return { ...dir, order: targetDirectory.order };
        if (dir.id === targetDirectory.id)
          return { ...dir, order: currentDirectory.order };
        return dir;
      })
    );
  };

  const handleChildClick = (child) => {
    setActiveChild(child);
    setEditorContent(child.content || ''); // Load existing content or default to empty
  };

  const handleSaveContent = async () => {
    if (!activeChild) return;

    const currentTimestamp = new Date(); // Current timestamp for logging

    try {
      // Update the content of the child directory
      await updateDoc(doc(db, 'directory', activeChild.id), {
        content: editorContent,
        lastUpdated: currentTimestamp, // Update the lastUpdated timestamp for the child
      });

      // If the child directory has a parent, update the parent's lastUpdated field as well
      if (activeChild.parent) {
        await updateDoc(doc(db, 'directory', activeChild.parent), {
          lastUpdated: currentTimestamp, // Update the lastUpdated timestamp for the parent
        });
      }

      // Update the state to reflect the saved content in the local state
      setDirectories((prevDirectories) =>
        prevDirectories.map((dir) =>
          dir.id === activeChild.id ? { ...dir, content: editorContent } : dir
        )
      );

      toast.success('Content saved!');
      console.log('Parent Id: ', activeChild.parent);
      console.log('Child Id: ', activeChild.id);
    } catch (error) {
      console.error('Error saving content: ', error);
      toast.error('Error saving content. Please try again.');
    }
  };

  const handleAddSubDirectory = async (parentId) => {
    const { value: subDirectoryName } = await MySwal.fire({
      title: 'Add Sub Directory',
      input: 'text',
      inputLabel: 'Enter the name of the subdirectory',
      inputPlaceholder: 'Sub Directory Name',
      showCancelButton: true,
      inputValidator: (value) => {
        if (!value) {
          return 'Subdirectory name cannot be empty!';
        }
      },
    });

    if (subDirectoryName) {
      const newDirectory = {
        name: subDirectoryName,
        parent: parentId,
        order: directories.filter((dir) => dir.parent === parentId).length,
      };
      const docRef = await addDoc(collection(db, 'directory'), newDirectory);

      setDirectories([...directories, { id: docRef.id, ...newDirectory }]);

      toast.success('Subdirectory added successfully!');
    }
  };

  return (
    <Layout isAdminPage={true}>
      {/* breadcrumbs */}
      <div className='row page-titles'>
        <div className='col-lg-3'>
          <ol className='breadcrumb'>
            <li className='breadcrumb-item active'>Administration</li>
            <li className='breadcrumb-item'>Directory</li>
          </ol>
        </div>
        <div className='col-lg-4'></div>
      </div>

      {/* Sidebar */}
      <div className='col-xl-12 col-lg-4'>
        <div className='row'>
          <div className='col-xl-4 col-lg-4'>
            <div className='card'>
              <div className='card-header bg-transparent'>
                <h5 className='mb-0'>Directory Administration</h5>
              </div>
              <div className='p-4 border-top'>
                {/* Create Root Library Section */}
                <div className='mb-4'>
                  <div className='d-flex align-items-center'>
                    <div className='input-group'>
                      <input
                        type='text'
                        className='form-control'
                        placeholder='Create Root Library'
                        value={newDirectoryName}
                        onChange={(e) => setNewDirectoryName(e.target.value)}
                      />
                      <button
                        className='btn btn-primary btn-xs'
                        type='button'
                        onClick={handleCreateDirectory}
                      >
                        Create
                      </button>
                    </div>
                  </div>
                </div>

                <hr />

                {loading ? (
                  <p>Loading...</p>
                ) : directories.length === 0 ? (
                  <p>No directories found.</p>
                ) : (
                  <ul className='list-unstyled categories-list'>
                    {directories
                      .filter((directory) => !directory.parent)
                      .sort((a, b) => a.order - b.order) // Sort parent directories by order
                      .map((directory) => (
                        <li key={directory.id}>
                          <div className='d-flex align-items-center'>
                            {/* Main Directory Section */}
                            <div className='col-10'>
                              <a
                                className='text-body fw-medium py-1 d-flex align-items-center'
                                data-bs-toggle='collapse'
                                href={`#categories-collapse-${directory.id}`}
                                role='button'
                                aria-expanded={
                                  activeDirectoryId === directory.id
                                }
                                aria-controls={`categories-collapse-${directory.id}`}
                                onClick={() => toggleAccordion(directory.id)}
                              >
                                <i className='mdi mdi-folder font-size-20 text-warning me-2'></i>
                                {directory.name}
                                <i
                                  className={`mdi ${
                                    activeDirectoryId === directory.id
                                      ? 'mdi-chevron-down'
                                      : 'mdi-chevron-up'
                                  } ms-auto accor-down-icon`}
                                ></i>
                              </a>
                            </div>

                            {/* Parent Action Menu */}
                            <div className='col-2 text-end'>
                              <div className='col d-flex justify-content-center'>
                                <div className='tooltip-container'>
                                  <div className='dropdown'>
                                    <button
                                      type='button'
                                      className='reaction-btn reaction-action'
                                      data-bs-toggle='dropdown'
                                      aria-expanded='false'
                                      aria-label='Actions'
                                      title='Actions'
                                    >
                                      <svg
                                        width='13'
                                        height='13'
                                        viewBox='0 0 24 24'
                                      >
                                        <g fill='none' fillRule='evenodd'>
                                          <rect width='24' height='24'></rect>
                                          <circle
                                            fill='#000'
                                            cx='5'
                                            cy='12'
                                            r='2'
                                          ></circle>
                                          <circle
                                            fill='#000'
                                            cx='12'
                                            cy='12'
                                            r='2'
                                          ></circle>
                                          <circle
                                            fill='#000'
                                            cx='19'
                                            cy='12'
                                            r='2'
                                          ></circle>
                                        </g>
                                      </svg>
                                    </button>
                                    <div className='dropdown-menu'>
                                      <button
                                        className='dropdown-item'
                                        type='button'
                                        onClick={() =>
                                          handleReorderDirectory(
                                            directory.id,
                                            'down',
                                            null
                                          )
                                        }
                                      >
                                        Move Down
                                      </button>
                                      <button
                                        className='dropdown-item'
                                        type='button'
                                        onClick={() =>
                                          handleAddSubDirectory(directory.id)
                                        }
                                      >
                                        Create Sub Directory
                                      </button>

                                      <div className='dropdown-divider'></div>

                                      <button
                                        className='dropdown-item'
                                        type='button'
                                        onClick={(e) => {
                                          setSelectedDirectoryId(directory.id);
                                        }}
                                        data-bs-toggle='modal'
                                        data-bs-target='#confirmDelete'
                                      >
                                        Delete
                                      </button>
                                    </div>
                                  </div>
                                  <span className='tooltip'>
                                    Sub level Actions
                                  </span>
                                </div>
                              </div>
                            </div>
                            {/* End Parent Action Menu */}
                          </div>

                          {/* Confirm Delete Dog File*/}
                          <Modal
                            id='confirmDelete'
                            title='Remove This Directory?'
                            onConfirm={async () => {
                              setIsDeleting(true);
                              try {
                                await handleDeleteDirectory(
                                  selectedDirectoryId
                                );
                              } finally {
                                setIsDeleting(false);
                              }
                            }}
                            confirmText='Yes, delete'
                            cancelText='Cancel'
                            loading={isDeleting}
                            loadingText='Deleting...'
                            size='medium'
                          >
                            <p>
                              Are you sure you want to delete this directory?
                            </p>
                          </Modal>

                          {/* Subdirectory Collapse Section */}
                          <div
                            className={`collapse ${
                              activeDirectoryId === directory.id ? 'show' : ''
                            }`}
                            id={`categories-collapse-${directory.id}`}
                          >
                            <div className='card border-0 shadow-none ps-2 mb-0'>
                              <ul className='list-unstyled mb-0'>
                                {directories
                                  .filter(
                                    (subDir) => subDir.parent === directory.id
                                  )
                                  .sort((a, b) => a.order - b.order)
                                  .map((subDir) => (
                                    <li
                                      key={subDir.id}
                                      className='d-flex align-items-center mt-2'
                                      onClick={() => handleChildClick(subDir)}
                                    >
                                      <span className='me-auto'>
                                        {subDir.name}
                                      </span>
                                      {/* Parent Action Menu */}
                                      <div className='col-2 text-end'>
                                        <div className='col d-flex justify-content-center'>
                                          <div className='tooltip-container'>
                                            <div className='dropdown'>
                                              <button
                                                type='button'
                                                className='reaction-btn reaction-action'
                                                data-bs-toggle='dropdown'
                                                aria-expanded='false'
                                                aria-label='Actions'
                                                title='Actions'
                                              >
                                                <svg
                                                  width='13'
                                                  height='13'
                                                  viewBox='0 0 24 24'
                                                >
                                                  <g
                                                    fill='none'
                                                    fillRule='evenodd'
                                                  >
                                                    <rect
                                                      width='24'
                                                      height='24'
                                                    ></rect>
                                                    <circle
                                                      fill='#000'
                                                      cx='5'
                                                      cy='12'
                                                      r='2'
                                                    ></circle>
                                                    <circle
                                                      fill='#000'
                                                      cx='12'
                                                      cy='12'
                                                      r='2'
                                                    ></circle>
                                                    <circle
                                                      fill='#000'
                                                      cx='19'
                                                      cy='12'
                                                      r='2'
                                                    ></circle>
                                                  </g>
                                                </svg>
                                              </button>
                                              <div className='dropdown-menu'>
                                                <button
                                                  className='dropdown-item'
                                                  type='button'
                                                  onClick={() =>
                                                    handleReorderDirectory(
                                                      subDir.id,
                                                      'up',
                                                      directory.id
                                                    )
                                                  }
                                                >
                                                  Move Up
                                                </button>
                                                <button
                                                  className='dropdown-item'
                                                  type='button'
                                                  onClick={() =>
                                                    handleReorderDirectory(
                                                      subDir.id,
                                                      'down',
                                                      directory.id
                                                    )
                                                  }
                                                >
                                                  Move Down
                                                </button>
                                                <button
                                                  className='dropdown-item'
                                                  type='button'
                                                  onClick={() =>
                                                    handleAddSubDirectory(
                                                      directory.id
                                                    )
                                                  }
                                                >
                                                  Create Sub Directory
                                                </button>
                                                <div className='dropdown-divider'></div>
                                                <button
                                                  className='dropdown-item'
                                                  type='button'
                                                  onClick={(e) => {
                                                    setSelectedDirectoryId(
                                                      subDir.id
                                                    );
                                                  }}
                                                  data-bs-toggle='modal'
                                                  data-bs-target='#confirmDelete'
                                                >
                                                  Delete
                                                </button>
                                              </div>
                                            </div>
                                            <span className='tooltip'>
                                              Top level Actions
                                            </span>
                                          </div>
                                        </div>
                                      </div>
                                      {/* End Parent Action Menu */}
                                    </li>
                                  ))}
                              </ul>
                            </div>
                          </div>
                        </li>
                      ))}
                  </ul>
                )}
              </div>
            </div>
          </div>

          <div className='col-xl-8 col-lg-6'>
            {activeChild && (
              <div className='card'>
                <div className='card-header bg-transparent'>
                  <h5 className='mb-0'>{activeChild.name}</h5>
                </div>
                <div className='p-4'>
                  <ReactQuill
                    value={editorContent}
                    onChange={setEditorContent}
                  />
                </div>
                <div className='p-4'>
                  <button
                    type='button'
                    className='btn btn-primary'
                    onClick={handleSaveContent}
                  >
                    Save
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </Layout>
  );
}

export default DirectoryAdmin;
